import React, { useEffect, useState } from 'react';
import { 
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Pagination,
  Chip,
  Tooltip,
  IconButton,
  Modal,
  Button,
  Skeleton
} from '@mui/material';
import { FaSyncAlt as RefreshIcon, FaInfoCircle as InfoIcon } from 'react-icons/fa';
import { getData } from '../../Webservice/webservice';

const PAGE_SIZE = 20;

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const handleOpenModal = (details) => {
    setSelectedDetails(details);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDetails(null);
  };

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const res = await getData('/logs/activity', { page, limit: PAGE_SIZE }, token, false);
      setLogs(res.logs || []);
      setMeta(res.meta || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      setError('Failed to fetch activity logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(meta.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page]);

  const handlePageChange = (event, newPage) => {
    setMeta((prev) => ({ ...prev, page: newPage }));
  };

  const getActionStyles = (action) => {
    const lower = (action || '').toLowerCase();
    switch (lower) {
      case 'create':
        return { 
          backgroundColor: 'rgba(40, 167, 69, 0.1)', 
          color: '#28a745',
          border: '1px solid rgba(40, 167, 69, 0.1)'
        };
      case 'update':
        return { 
          backgroundColor: 'rgba(255, 193, 7, 0.1)', 
          color: '#ffc107',
          border: '1px solid rgba(255, 193, 7, 0.1)'
        };
      case 'delete':
        return { 
          backgroundColor: 'rgba(220, 53, 69, 0.1)', 
          color: '#dc3545',
          border: '1px solid rgba(220, 53, 69, 0.1)'
        };
      case 'login success':
      case 'success':
        return { 
            backgroundColor: 'rgba(40, 167, 69, 0.1)', 
            color: '#28a745',
            border: '1px solid rgba(40, 167, 69, 0.1)'
        };
      case 'failed login':
      case 'failure':
      case 'error':
        return { 
            backgroundColor: 'rgba(220, 53, 69, 0.1)', 
            color: '#dc3545',
            border: '1px solid rgba(220, 53, 69, 0.1)'
        };
      case 'logout':
        return { 
            backgroundColor: 'rgba(13, 110, 253, 0.1)', 
            color: '#0d6efd',
            border: '1px solid rgba(13, 110, 253, 0.1)'
        };
      default:
        return { 
          backgroundColor: 'rgba(108, 117, 125, 0.1)', 
          color: '#6c757d',
          border: '1px solid rgba(108, 117, 125, 0.1)'
        };
    }
  };

  const formatUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return userAgent.substring(0, 30) + (userAgent.length > 30 ? '...' : '');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 700, color: "#1a1a1a" }}>
          Activity Log
        </Typography>
        <Box>
            <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchLogs(meta.page)}
                disabled={loading}
                sx={{
                fontFamily: 'Mulish, sans-serif',
                textTransform: 'none',
                color: '#6c757d',
                borderColor: '#dee2e6',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#ced4da',
                    color: '#495057'
                }
                }}
            >
                Refresh
            </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table sx={{ minWidth: 650 }} aria-label="activity log table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>Date & Time</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>Entity</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>Details</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>IP Address</TableCell>
              <TableCell sx={{ fontWeight: 700, fontFamily: 'Mulish, sans-serif', fontSize: '0.875rem', color: '#495057', textTransform: 'uppercase' }}>Browser</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              Array.from({ length: 6 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton width={140} />
                    <Skeleton width={100} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={160} />
                    <Skeleton width={120} />
                  </TableCell>
                  <TableCell>
                    <Skeleton width={100} height={28} />
                  </TableCell>
                    <TableCell>
                      <Skeleton width={140} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={160} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width={120} />
                    </TableCell>
                </TableRow>
              ))
            )}
            
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.6 }}>
                    <img src="/img/nodata.png" alt="No Data" style={{ width: '120px', marginBottom: '16px' }} />
                    <Typography variant="body1" color="textSecondary" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                        No activity logs found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!loading && logs.map((log) => (
              <TableRow 
                key={log._id} 
                hover
                sx={{ 
                  '&:hover': { backgroundColor: '#f8f9fa' },
                  '& td': { borderBottom: '1px solid #f1f3f5', py: 2 }
                }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{fontFamily:'Mulish, sans-serif', fontWeight: 600, color: '#212529'}}>
                    {new Date(log.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{fontFamily:'Mulish, sans-serif'}}>
                    {new Date(log.createdAt).toLocaleTimeString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 , fontFamily:'Mulish, sans-serif', color: '#212529'}}>
                    {log.userId ? `${log.userId.firstName} ${log.userId.lastName}` : 'System'}
                  </Typography>
                  {log.userId && (
                    <Typography variant="caption" color="textSecondary" sx={{fontFamily:'Mulish, sans-serif'}}>
                      {log.userId.email}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={log.action} 
                    size="small"
                    sx={{
                      fontFamily:'Mulish, sans-serif',
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      height: '24px',
                      ...getActionStyles(log.action)
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{fontFamily:'Mulish, sans-serif', color: '#495057'}}>
                    {log.entity}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View details" arrow>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={() => handleOpenModal(log.details)}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150, fontFamily:'Mulish, sans-serif', textDecoration: 'none', color: '#0d6efd', fontWeight: 500 }}>
                        {log.details?.email || 'View details'}
                      </Typography>
                      <InfoIcon style={{ marginLeft: 6, color: '#0d6efd' }} size={12} />
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{fontFamily:'Mulish, sans-serif', color: '#495057'}}>
                    {log.ipAddress}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={log.userAgent} arrow>
                    <Typography variant="body2" sx={{fontFamily:'Mulish, sans-serif', color: '#495057'}}>
                      {formatUserAgent(log.userAgent)}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
        <Pagination
          count={meta.totalPages}
          page={meta.page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          disabled={loading}
          showFirstButton
          showLastButton
          sx={{ '& .MuiPaginationItem-root': { fontFamily: 'Mulish, sans-serif' } }}
        />
      </Box>

      {meta.total > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}>
          Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
        </Typography>
      )}

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="details-modal-title"
        aria-describedby="details-modal-description"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box sx={{
          bgcolor: 'background.paper',
          p: 4,
          borderRadius: '16px',
          boxShadow: 24,
          maxWidth: 600,
          width: '90%',
          outline: 'none'
        }}>
          <Typography id="details-modal-title" variant="h6" component="h2" sx={{ mb: 3, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
            Details
          </Typography>
          <Box id="details-modal-description" sx={{ maxHeight: 300, overflowY: 'auto', mb: 3 }}>
            {selectedDetails ? (
              <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {Object.entries(selectedDetails).map(([key, value]) => (
                  <Box key={key} sx={{ display: 'flex', borderBottom: '1px solid #f1f3f5', pb: 1 }}>
                    <Typography variant="subtitle2" sx={{ width: '170px', flexShrink: 0, fontWeight: 700, fontFamily: 'Mulish, sans-serif', color: '#495057' }}>
                      {key}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif', wordBreak: 'break-all', color: '#212529' }}>
                      {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
                <Typography sx={{ fontFamily: 'Mulish, sans-serif' }}>No details available.</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
                onClick={handleCloseModal} 
                variant="contained" 
                sx={{ 
                    bgcolor: '#2B333A', 
                    color: 'white', 
                    textTransform: 'none',
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: '#1a1f24' }
                }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ActivityLog;